import { useState } from 'react'
import {
  Search, ChevronDown, ChevronRight,
  // Templates
  LayoutTemplate,
  // Pipeline
  Workflow, TrendingUp, Cloud,
  // Control
  Clock, GitBranch, Repeat, GitMerge, Save, Bell, Settings2,
  // Ingest
  Upload, FileUp,
  // Parse
  FileCode, FileSymlink, Database,
  // Analyze
  Layers, GitFork, Variable, BarChart3,
  // Refactor
  Wand2, Minimize2, AlignLeft, PackagePlus,
  // Map
  ArrowRightLeft, Globe,
  // Decompose
  LayoutGrid, CircleDot, Flame, Scissors,
  // Data
  TableProperties, Boxes, ArrowLeftRight,
  // Discover
  FileText, AlertTriangle, ClipboardList,
  // Generate
  FolderTree, Cog, Plug, Code2, TestTube2, Component, Package,
  // Output
  Archive, FileSpreadsheet, UserCheck, Download,
  // AI
  Sparkles, MessageSquareText, ScanSearch, BotMessageSquare
} from 'lucide-react'
import { NODE_REGISTRY, NODE_CATEGORIES, CATEGORY_COLORS } from '../../data/nodeRegistry'
import Tooltip from '../shared/Tooltip'
import { NODE_TOOLTIPS } from '../../constants/tooltipContent'
import './NodePalette.css'

// Icon mapping
const ICONS = {
  // Templates
  LayoutTemplate,
  // Pipeline
  Workflow, TrendingUp, Cloud,
  // Control
  Clock, GitBranch, Repeat, GitMerge, Save, Bell, Settings2,
  // Ingest
  Upload, FileUp,
  // Parse
  FileCode, FileSymlink, Database,
  // Analyze
  Layers, GitFork, Variable, BarChart3,
  // Refactor
  Wand2, Minimize2, AlignLeft, PackagePlus,
  // Map
  ArrowRightLeft, Globe,
  // Decompose
  LayoutGrid, CircleDot, Flame, Scissors,
  // Data
  TableProperties, Boxes, ArrowLeftRight,
  // Discover
  FileText, AlertTriangle, ClipboardList, Search,
  // Generate
  FolderTree, Cog, Plug, Code2, TestTube2, Component, Package,
  // Output
  Archive, FileSpreadsheet, UserCheck, Download,
  // AI
  Sparkles, MessageSquareText, ScanSearch, BotMessageSquare
}

function PaletteItem({ nodeType }) {
  const node = NODE_REGISTRY[nodeType]
  if (!node) return null

  const Icon = ICONS[node.icon] || FileCode
  const isDisabled = node.disabled === true

  const handleDragStart = (e) => {
    if (isDisabled) {
      e.preventDefault()
      return
    }
    e.dataTransfer.setData('application/reactflow', nodeType)
    e.dataTransfer.effectAllowed = 'move'
  }

  // Get tooltip content from registry
  const tooltipContent = NODE_TOOLTIPS[nodeType] || {}
  const simpleTooltip = isDisabled ? `${node.description} (Coming soon)` : (tooltipContent.simple || node.description)
  const advancedTooltip = isDisabled ? `${node.description} (Coming soon)` : (tooltipContent.advanced || node.description)

  return (
    <Tooltip simple={simpleTooltip} advanced={advancedTooltip} placement="right" delay={200}>
      <div
        className={`palette-item ${isDisabled ? 'disabled' : ''}`}
        draggable={!isDisabled}
        onDragStart={handleDragStart}
      >
        <span className="palette-item-icon" style={{ color: isDisabled ? 'var(--text-muted)' : node.color }}>
          <Icon size={16} />
        </span>
        <span className="palette-item-label">{node.label}</span>
      </div>
    </Tooltip>
  )
}

function PaletteCategory({ categoryId, category, expanded, onToggle }) {
  const Icon = ICONS[category.icon] || FileCode

  return (
    <div className="palette-category">
      <button
        className="palette-category-header"
        onClick={() => onToggle(categoryId)}
      >
        <span className="category-indicator" style={{ backgroundColor: category.color }} />
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Icon size={16} style={{ color: category.color }} />
        <span className="category-label">{category.label}</span>
        <span className="category-count">{category.nodes.length}</span>
      </button>

      {expanded && (
        <div className="palette-category-items">
          {category.nodes.map(nodeType => (
            <PaletteItem key={nodeType} nodeType={nodeType} />
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Read-only category label (no expand, no subnodes) ── */
function PaletteCategoryLabel({ category }) {
  const Icon = ICONS[category.icon] || FileCode
  return (
    <div className="palette-category">
      <div className="palette-category-header category-label-only">
        <span className="category-indicator" style={{ backgroundColor: category.color }} />
        <Icon size={16} style={{ color: category.color }} />
        <span className="category-label">{category.label}</span>
      </div>
    </div>
  )
}

function NodePalette() {
  const [expandedCategories, setExpandedCategories] = useState({
    templates: true,
  })

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }))
  }

  // Templates category stays fully functional
  const templatesCategory = NODE_CATEGORIES.templates
    ? { id: 'templates', ...NODE_CATEGORIES.templates }
    : null

  // All other categories render as labels only
  const labelCategories = Object.entries(NODE_CATEGORIES)
    .filter(([id]) => id !== 'templates')
    .map(([id, category]) => ({ id, ...category }))

  return (
    <div className="node-palette">
      <div className="palette-header">
        <h3>Nodes</h3>
      </div>

      {/* ── Search — commented out (not useful when only templates are expandable)
      <div className="palette-search">
        <Search size={16} />
        <input
          type="text"
          placeholder="Search nodes..."
          value=""
          onChange={() => {}}
        />
      </div>
      ── */}

      <div className="palette-categories">
        {/* Templates — fully functional, expandable, draggable */}
        {templatesCategory && (
          <PaletteCategory
            key={templatesCategory.id}
            categoryId={templatesCategory.id}
            category={templatesCategory}
            expanded={expandedCategories.templates}
            onToggle={toggleCategory}
          />
        )}

        {/* All other categories — label only, no expand, no subnodes */}
        {labelCategories.map(category => (
          <PaletteCategoryLabel key={category.id} category={category} />
        ))}
      </div>

      <div className="palette-footer">
        <span className="drag-hint">Drag templates to canvas</span>
      </div>
    </div>
  )

  /* ── Original NodePalette body — commented out for future re-enablement ──
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedCategories, setExpandedCategories] = useState({
    templates: true,
    pipeline: true,
    ingest: true,
    parse: false,
    analyze: false,
    refactor: false,
    map: false,
    decompose: false,
    data: false,
    discover: false,
    architecture: false,
    generate: false,
    output: false,
    ai: false,
    control: false,
  })

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }))
  }

  const filteredCategories = Object.entries(NODE_CATEGORIES).map(([id, category]) => {
    if (!searchTerm) return { id, ...category }
    const filteredNodes = category.nodes.filter(nodeType => {
      const node = NODE_REGISTRY[nodeType]
      return node && (
        node.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })
    return { id, ...category, nodes: filteredNodes }
  }).filter(category => category.nodes.length > 0)

  return (
    <div className="node-palette">
      <div className="palette-header">
        <h3>Nodes</h3>
      </div>
      <div className="palette-search">
        <Search size={16} />
        <input
          type="text"
          placeholder="Search nodes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="palette-categories">
        {filteredCategories.map(category => (
          <PaletteCategory
            key={category.id}
            categoryId={category.id}
            category={category}
            expanded={searchTerm ? true : expandedCategories[category.id]}
            onToggle={toggleCategory}
          />
        ))}
      </div>
      <div className="palette-footer">
        <span className="drag-hint">Drag nodes to canvas</span>
      </div>
    </div>
  )
  ── */
}

export default NodePalette
